import { HttpClient } from "../../http/HttpClient";
import { IOTPService } from "@/core/requests/services/OTPService";
import {
  SendOTPRequest,
  SendOTPResponse,
  VerifyOTPRequest,
  VerifyOTPResponse,
} from "@/core/requests/entities/OTP";
import {
  RepositoryResult,
  RepositoryResultFactory,
} from "@/core/shared/types/RepositoryResult";
import { ApiResponse } from "../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../http/services/handleApiResponse/handleApiResponse";
import {
  sendOTPUrl,
  verifyOTPUrl,
} from "../../api/otpBackendEndpoints/otpBackendEndpoints";
import handleRequestError from "../../http/services/handleRequestError/handleRequestError";

export default class OTPRepositoryBackend implements IOTPService {
  constructor(private readonly httpClient: HttpClient) {}

  async sendOTP(
    request: SendOTPRequest
  ): Promise<RepositoryResult<SendOTPResponse>> {
    try {
      const url = sendOTPUrl();

      const requestBody = {
        destination: request.destination,
        channel: request.channel.toUpperCase(),
        operationType: request.operationType.toUpperCase(),
        ipAddress: "",
        deviceFingerprint: request.deviceFingerprint,
        operationDetails: request.operationDetails,
      };

      const response = await this.httpClient.post<
        ApiResponse<{ challengeId: string; status: string; message: string }>
      >({
        url,
        body: requestBody,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<{
        challengeId: string;
        status: string;
        message: string;
      }>(response);

      if (result.success) {
        const mapped: SendOTPResponse = {
          code: response.code,
          message: response.message,
          data: {
            challengeId: result.data?.challengeId || "",
            status: result.data?.status || "",
            message: result.data?.message || "",
          },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response)
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>)
      );
    }
  }

  async verifyOTP(
    request: VerifyOTPRequest
  ): Promise<RepositoryResult<VerifyOTPResponse>> {
    try {
      const url = verifyOTPUrl();
      const payload = {
        challengeId: request.challengeId,
        code: request.code,
      };

      const response = await this.httpClient.post<ApiResponse<boolean>>({
        url,
        body: payload,
        withAuth: true,
      });

      const result = ApiResponseHandler.handleResponse<boolean>(response);

      if (result.success) {
        const mapped: VerifyOTPResponse = {
          code: response.code,
          message: response.message,
          data: {
            verified: result.data || false,
          },
        };
        return RepositoryResultFactory.success(mapped);
      }

      return RepositoryResultFactory.error(
        ApiResponseHandler.createExceptionFromResponse(response)
      );
    } catch (error) {
      return RepositoryResultFactory.error(
        handleRequestError(error as ApiResponse<unknown>)
      );
    }
  }
}
